import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { signOut } from 'firebase/auth';
import './Dashboard.css';
import Categories from './components/categories/Categories';
import Accounts from './components/accounts/Accounts';
import AccountTransactions from './components/accounts/AccountTransactions';
import Budgets from './components/budgets/Budgets';
import Transactions from './components/transactions/Transactions';
import BalanceOverview from './components/widgets/BalanceOverview';
import ChartOfAccounts from './components/settings/ChartOfAccounts';
import UsersManagement from './components/settings/UsersManagement';
import FilesCabinet from './components/cabinet/FilesCabinet';
import Periods from './components/periods/Periods';
import TransactionTypeModal from './components/transactions/TransactionTypeModal';
import AddCategoryDialog from './components/categories/AddCategoryDialog';
import AddAccountDialog from './components/accounts/AddAccountDialog';
import CategoryGroups from './components/categories/CategoryGroups';
import Payments from './components/transactions/Payments';
import Transfers from './components/transactions/Transfers';

function Dashboard({ selectedProfile, onChangeProfile }) {
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [settingsSubmenu, setSettingsSubmenu] = useState(null);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);

  // Transaction form state
  const [transactionAmount, setTransactionAmount] = useState('');
  const [transactionCategory, setTransactionCategory] = useState('');
  const [transactionDescription, setTransactionDescription] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);

  // Budget form state
  const [budgetCategory, setBudgetCategory] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetPeriod, setBudgetPeriod] = useState('monthly');

  const user = auth.currentUser;

  // Load transactions
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'transactions'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const transactionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(transactionsData);
    });
    return unsubscribe;
  }, [user]);

  // Load budgets
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'budgets'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const budgetsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBudgets(budgetsData);
    });
    return unsubscribe;
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'transactions'), {
        userId: user.uid,
        amount: parseFloat(transactionAmount),
        category: transactionCategory,
        description: transactionDescription,
        date: transactionDate,
        createdAt: new Date()
      });
      setTransactionAmount('');
      setTransactionCategory('');
      setTransactionDescription('');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setShowTransactionForm(false);
    } catch (err) {
      alert('Error adding transaction: ' + err.message);
    }
  };

  const handleAddBudget = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'budgets'), {
        userId: user.uid,
        category: budgetCategory,
        amount: parseFloat(budgetAmount),
        period: budgetPeriod,
        createdAt: new Date()
      });
      setBudgetCategory('');
      setBudgetAmount('');
      setBudgetPeriod('monthly');
      setShowBudgetForm(false);
    } catch (err) {
      alert('Error adding budget: ' + err.message);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (window.confirm('Delete this transaction?')) {
      await deleteDoc(doc(db, 'transactions', id));
    }
  };

  const handleDeleteBudget = async (id) => {
    if (window.confirm('Delete this budget?')) {
      await deleteDoc(doc(db, 'budgets', id));
    }
  };

  const handleLogout = () => {
    signOut(auth);
  };

  const toggleDropdown = (dropdownName, e) => {
    e.stopPropagation();
    setOpenDropdown(prev =>
      prev === dropdownName ? null : dropdownName
    );
  };

  const handleViewAccountTransactions = (account) => {
    setSelectedAccount(account);
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
  };

  const totalSpent = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalBudget = budgets.reduce((sum, b) => sum + (b.amount || 0), 0);

  return (
    <div className="dashboard-container">
      {/* Top White Bar - User Controls */}
      <div className="top-bar">
        <div className="top-bar-left">
          <div className="logo">VAULT</div>
          <div className="company-name">Budgeting WebApp</div>
        </div>
        <div className="top-bar-right">
          <span className="notification-icon">&#128276;</span>
          <div className="user-section">
            <div className="user-avatar">
              {selectedProfile?.name?.substring(0, 2).toUpperCase() || 
               user?.displayName?.substring(0, 2).toUpperCase() || 
               user?.email?.substring(0, 2).toUpperCase() || 'U'}
            </div>
            <div className="user-info">
              <div className="dashboard-user-name">{selectedProfile?.name || user?.displayName || user?.email}</div>
              <div className="dashboard-user-role">{selectedProfile?.type || 'User'}</div>
            </div>
          </div>
          <button className="btn-change-profile" onClick={onChangeProfile}>
            Change Profile
          </button>
          <button className="btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* Horizontal Navigation Bar */}
      <div className="nav-bar">
        <div className="nav-items">
          <div 
            className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveSection('dashboard'); setSelectedAccount(null); }}
          >
            Dashboard
          </div>
          
          {/* TRANSACTIONS DROPDOWN */}
          <div className={`nav-item-wrapper ${activeSection === 'transactions' ? 'active' : ''}`}>
            <div 
              className="nav-item"
              onClick={(e) => toggleDropdown('transactions', e)}
            >
              Transactions
            </div>
            {openDropdown === 'transactions' && (
              <div className="nav-dropdown show" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setShowTransactionForm(true); setOpenDropdown(null); }}
                >
                  Add Transaction
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('transactions'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Transactions List
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('payments'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Payments
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('transfers'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Transfers
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setOpenDropdown(null); }}
                >
                  Installments
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setOpenDropdown(null); }}
                >
                  Accrued Expenses
                </div>
              </div>
            )}
          </div>

          {/* BUDGETS DROPDOWN */}
          <div className={`nav-item-wrapper ${activeSection === 'budgets' ? 'active' : ''}`}>
            <div 
              className="nav-item"
              onClick={(e) => toggleDropdown('budgets', e)}
            >
              Budgets
            </div>
            {openDropdown === 'budgets' && (
              <div className="nav-dropdown show" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('budgets'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Budgets List
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('budgets'); setShowBudgetForm(true); setOpenDropdown(null); }}
                >
                  Create Budget
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('budgets'); setShowBudgetForm(true); setOpenDropdown(null); }}
                >
                  Calculator Budget
                </div>
              </div>
            )}
          </div>

          <div 
            className={`nav-item ${activeSection === 'analytics' ? 'active' : ''}`}
            onClick={() => { setActiveSection('analytics'); setSelectedAccount(null); }}
          >
            Analytics
          </div>

          {/* ACCOUNTS DROPDOWN */}
          <div className={`nav-item-wrapper ${activeSection === 'accounts' ? 'active' : ''}`}>
            <div 
              className="nav-item"
              onClick={(e) => toggleDropdown('accounts', e)}
            >
              Accounts
            </div>
            {openDropdown === 'accounts' && (
              <div className="nav-dropdown show" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setShowAccountForm(true); setOpenDropdown(null); }}
                >
                  Add Account
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('accounts'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Account List
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('category-groups'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Group Accounts
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setOpenDropdown(null); }}
                >
                  Goals & SubAccounts
                </div>
              </div>
            )}
          </div>

          {/* CATEGORIES DROPDOWN */}
          <div className={`nav-item-wrapper ${activeSection === 'categories' ? 'active' : ''}`}>
            <div 
              className="nav-item"
              onClick={(e) => toggleDropdown('categories', e)}
            >
              Categories
            </div>
            {openDropdown === 'categories' && (
              <div className="nav-dropdown show" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setShowCategoryForm(true); setOpenDropdown(null); }}
                >
                  Add Category
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('categories'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Category List
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('category-groups'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Category Groups
                </div>
              </div>
            )}
          </div>

          <div 
            className={`nav-item ${activeSection === 'periods' ? 'active' : ''}`}
            onClick={() => { setActiveSection('periods'); setSelectedAccount(null); }}
          >
            Periods
          </div>

          <div 
            className={`nav-item ${activeSection === 'files' ? 'active' : ''}`}
            onClick={() => { setActiveSection('files'); setSelectedAccount(null); }}
          >
            Files Cabinet
          </div>

          {/* SETTINGS DROPDOWN */}
          <div className={`nav-item-wrapper ${activeSection === 'settings' ? 'active' : ''}`}>
            <div 
              className="nav-item"
              onClick={(e) => toggleDropdown('settings', e)}
            >
              Settings
            </div>
            {openDropdown === 'settings' && (
              <div className="nav-dropdown show" onClick={(e) => e.stopPropagation()}>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('settings'); setSettingsSubmenu('chart-of-accounts'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Chart of Accounts
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('periods'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Periods
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setOpenDropdown(null); }}
                >
                  Imports & Exports
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('settings'); setSettingsSubmenu('users'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Users Management
                </div>
                <div 
                  className="nav-dropdown-item"
                  onClick={() => { setActiveSection('settings'); setSettingsSubmenu('preferences'); setSelectedAccount(null); setOpenDropdown(null); }}
                >
                  Preferences
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="content">
        {activeSection === 'dashboard' && (
          <>
            <div className="welcome-banner">
              <h2>Welcome back, {selectedProfile?.name}!</h2>
              <p>Here's your financial overview for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>

            {/* 3-Column Dashboard Layout */}
            <div className="dashboard-grid">
              {/* Left Column */}
              <div className="dashboard-column left">
                <div className="widget-placeholder">
                  <p>More widgets coming soon...</p>
                </div>
              </div>

              {/* Center Column - Wider */}
              <div className="dashboard-column center">
                <BalanceOverview />
              </div>

              {/* Right Column */}
              <div className="dashboard-column right">
                {/* Placeholder for future widgets */}
                <div className="widget-placeholder">
                  <p>More widgets coming soon...</p>
                </div>
              </div>
            </div>
          </>
        )}

        {activeSection === 'transactions' && (
          <Transactions 
            triggerAddTransaction={showTransactionForm}
            onTransactionFormOpen={() => setShowTransactionForm(false)}
          />
        )}

        {activeSection === 'payments' && <Payments />}

        {activeSection === 'transfers' && <Transfers />}

        {activeSection === 'budgets' && <Budgets />}

        {activeSection === 'analytics' && (
          <div className="section-content">
            <h2>Analytics</h2>
            <p className="coming-soon">Analytics coming soon...</p>
          </div>
        )}

        {activeSection === 'accounts' && (
          selectedAccount ? (
            <AccountTransactions 
              account={selectedAccount} 
              onBack={handleBackToAccounts}
            />
          ) : (
            <Accounts onViewTransactions={handleViewAccountTransactions} />
          )
        )}

        {activeSection === 'categories' && <Categories />}

        {activeSection === 'category-groups' && <CategoryGroups />}

        {activeSection === 'periods' && <Periods />}

        {activeSection === 'files' && <FilesCabinet />}

        {activeSection === 'settings' && (
          <div className="section-content">
            {!settingsSubmenu && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <h2>Settings</h2>
                <p>Please select an option from the Settings menu above</p>
              </div>
            )}
            {settingsSubmenu === 'chart-of-accounts' && <ChartOfAccounts />}
            {settingsSubmenu === 'users' && <UsersManagement onClose={() => setSettingsSubmenu(null)} selectedProfile={selectedProfile} />}
            {settingsSubmenu === 'preferences' && (
              <div>
                <h2>Preferences</h2>
                <p className="coming-soon">Preferences coming soon...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Global Transaction Modal */}
      {showTransactionForm && (
        <TransactionTypeModal onClose={() => setShowTransactionForm(false)} />
      )}

      {/* Global Category Modal */}
      {showCategoryForm && (
        <AddCategoryDialog 
        isOpen={showCategoryForm}
        onClose={() => setShowCategoryForm(false)}
        onSuccess={() => setShowCategoryForm(false)}
        editCategory={null}
        />
      )}

      {/* Global Account Modal */}
      {showAccountForm && (
        <AddAccountDialog 
        isOpen={showAccountForm}
        onClose={() => setShowAccountForm(false)}
        onSuccess={() => setShowAccountForm(false)}
        editAccount={null}
        />
      )}
    </div>
  );
}

export default Dashboard;